import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  Zap, Check, Shield, Sparkles, ArrowRight, 
  Users, TrendingUp, Code2, Rocket, Star,
  Play, ChevronRight
} from 'lucide-react';

const PremiumLandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // If user is authenticated, redirect to dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen text-gray-900 overflow-x-hidden bg-gradient-to-b from-white via-white via-50% to-orange-50/50">
      {/* Premium Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/70 backdrop-blur-xl border-b border-gray-200/30 shadow-lg shadow-gray-900/5' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-8 py-5">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
              <img src="/skillerate-icon.svg" alt="Skillerate" className="w-10 h-10 transition-transform group-hover:scale-105" />
              <span className="text-xl font-semibold text-gray-900">Skillerate</span>
            </div>

            {/* Navigation Links */}
            <div className="hidden lg:flex items-center gap-10">
              <button 
                onClick={() => scrollToSection('benefits')}
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
              >
                Benefits
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
              >
                Pricing
              </button>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="px-6 py-2.5 border-2 border-orange-400 text-orange-600 rounded-full font-medium text-sm hover:border-orange-500 hover:bg-orange-50 transition-all duration-300"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                className="px-6 py-2.5 bg-gradient-to-r from-[#e57f3f] to-[#c43408] text-white rounded-full font-medium text-sm hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Center Aligned, Premium Design */}
      <section className="relative pt-40 pb-32 px-8 overflow-visible bg-transparent">
        {/* Centered Gradient Orb - Behind CTA and Mockup Only */}
        
        {/* Main gradient orb positioned low - behind buttons and mockup */}
        <div className="absolute top-[60%] left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-gradient-radial from-orange-200/45 via-orange-100/25 via-50% to-transparent rounded-[50%] blur-3xl" />
        
        {/* Secondary softer layer for depth */}
        <div className="absolute top-[65%] left-1/2 -translate-x-1/2 w-[1100px] h-[700px] bg-gradient-radial from-[#e57f3f]/15 via-orange-50/8 via-60% to-transparent rounded-[50%] blur-3xl" />
        
        {/* Subtle mesh overlay for texture */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(229, 127, 63, 0.08) 1px, transparent 0)',
            backgroundSize: '64px 64px'
          }} />
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Centered Content */}
          <div className="text-center space-y-10 mb-16">
            <div className="inline-block">
              <div className="px-4 py-2 bg-white/80 backdrop-blur-sm border border-orange-200/50 rounded-full text-sm font-medium text-orange-600 shadow-sm">
                ✨ The Claude Skills Marketplace
              </div>
            </div>

            <h1 className="text-[72px] font-bold leading-[1.1] tracking-tight">
              Build Skills That
              <br />
              <span className="bg-gradient-to-r from-[#e57f3f] to-[#c43408] bg-clip-text text-transparent">
                Amplify Claude
              </span>
            </h1>

            <p className="text-[18px] text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Transform your expertise into validated, reusable Claude Skills. No coding required—just your knowledge and our visual builder.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button
                onClick={() => navigate('/signup')}
                className="group px-8 py-4 bg-gradient-to-r from-[#e57f3f] to-[#c43408] text-white rounded-full font-semibold text-base hover:shadow-2xl hover:shadow-orange-500/30 transition-all duration-500 flex items-center gap-3"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="px-8 py-4 border-2 border-orange-400 text-orange-600 rounded-full font-semibold text-base hover:border-orange-500 hover:bg-orange-50 transition-all duration-300"
              >
                Sign In
              </Button>
            </div>
          </div>

          {/* Centered Floating Dashboard Mockup */}
          <div className="relative max-w-5xl mx-auto">
            <div className="relative">
              {/* Floating Effect Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 to-amber-400/20 blur-3xl rounded-3xl" />
              
              {/* Dashboard Screenshot - Clean with proper radius */}
              <div className="relative">
                {/* Subtle background glow for depth */}
                <div className="absolute -inset-4 bg-gradient-to-br from-orange-400/10 to-amber-400/10 blur-2xl" />
                
                {/* Dashboard image with enhanced radius and no border */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-gray-900/20">
                  <img 
                    src="/dashboard-hero.png" 
                    alt="Skillerate Dashboard" 
                    className="w-full h-auto rounded-3xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Premium Cards with Glow */}
      <section id="benefits" className="relative py-32 px-8 overflow-hidden">
        {/* Gradient background layer - extends from top of page seamlessly */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-100/30 to-white" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-gradient-radial from-orange-200/35 via-orange-100/20 to-transparent rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Why Choose Skillerate?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Everything you need to build, validate, and share Claude Skills
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Benefit 1 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-100/50 to-amber-100/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
              <div className="relative bg-white rounded-3xl p-10 border border-gray-100 shadow-lg shadow-gray-900/5 hover:shadow-xl hover:shadow-gray-900/10 transition-all duration-500">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e57f3f] to-[#c43408] rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-orange-500/25">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Lightning-Fast Creation</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Build production-ready skills in minutes with our intuitive visual editor. Your expertise, packaged into Claude-powered workflows.
                </p>
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-100/50 to-amber-100/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
              <div className="relative bg-white rounded-3xl p-10 border border-gray-100 shadow-lg shadow-gray-900/5 hover:shadow-xl hover:shadow-gray-900/10 transition-all duration-500">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e57f3f] to-[#c43408] rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-orange-500/25">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Claude-Validated Quality</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Every skill is automatically tested by Claude before publishing. Only skills that work flawlessly reach the marketplace.
                </p>
              </div>
            </div>

            {/* Benefit 3 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-100/50 to-amber-100/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
              <div className="relative bg-white rounded-3xl p-10 border border-gray-100 shadow-lg shadow-gray-900/5 hover:shadow-xl hover:shadow-gray-900/10 transition-all duration-500">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e57f3f] to-[#c43408] rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-orange-500/25">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Build Your Reputation</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Showcase your skills to thousands of Claude users. Every published skill strengthens your brand as an AI expert.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Visual Workflow */}
      <section id="how-it-works" className="relative py-32 px-8 overflow-hidden">
        {/* Layered gradient background - cohesive orange */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 via-orange-100/20 to-white" />
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-radial from-orange-200/30 via-orange-100/15 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-orange-200/25 via-orange-100/12 to-transparent rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Your Skill Stack Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              From ideation to Claude mastery in four powerful steps
            </p>
          </div>

          <div className="relative space-y-24">
            {/* Connecting Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-orange-200 via-amber-200 to-orange-200 hidden lg:block" />

            {/* Step 1: Discover - Orange background chat interface */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-[#e57f3f] text-white flex items-center justify-center text-xl font-bold">
                    1
                  </div>
                </div>
                <h3 className="text-3xl font-bold mb-4">
                  Discover Your Next Skill
                </h3>
                <p className="text-lg text-gray-600">
                  Not sure what to build? Chat with our AI assistant to explore 
                  possibilities. From your expertise to market needs, we'll help you 
                  discover the perfect skill to create.
                </p>
              </div>
              
              <div 
                className="relative rounded-2xl overflow-hidden bg-white"
                style={{
                  boxShadow: `
                    0 20px 25px -5px rgba(0, 0, 0, 0.1),
                    0 50px 100px -20px rgba(0, 0, 0, 0.15),
                    0 30px 60px -30px rgba(229, 127, 63, 0.2)
                  `
                }}
              >
                <img 
                  src="/images/journey/1-discover-your-next-skill.jpg"
                  alt="Discover Your Next Skill - AI chat interface"
                  className="w-full h-auto block"
                  style={{ borderRadius: '1rem' }}
                />
              </div>
            </div>

            {/* Step 2: Build - Step 1 of 4 form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              <div 
                className="relative rounded-2xl overflow-hidden bg-white order-1 md:order-1"
                style={{
                  boxShadow: `
                    0 20px 25px -5px rgba(0, 0, 0, 0.1),
                    0 50px 100px -20px rgba(0, 0, 0, 0.15),
                    0 30px 60px -30px rgba(229, 127, 63, 0.2)
                  `
                }}
              >
                <img 
                  src="/images/journey/2-build-with-ai-assistance.jpg"
                  alt="Build with AI Assistance - Skill builder form"
                  className="w-full h-auto block"
                  style={{ borderRadius: '1rem' }}
                />
              </div>
              
              <div className="order-2 md:order-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-[#e57f3f] text-white flex items-center justify-center text-xl font-bold">
                    2
                  </div>
                </div>
                <h3 className="text-3xl font-bold mb-4">
                  Build with AI Assistance
                </h3>
                <p className="text-lg text-gray-600">
                  Use our intuitive Skill Builder to craft your skill. Add your inputs, 
                  or let AI enhance your discovery session ideas into production-ready 
                  skills with smart suggestions.
                </p>
              </div>
            </div>

            {/* Step 3: Organize - Category grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-[#e57f3f] text-white flex items-center justify-center text-xl font-bold">
                    3
                  </div>
                </div>
                <h3 className="text-3xl font-bold mb-4">
                  Organize & Share
                </h3>
                <p className="text-lg text-gray-600">
                  Stack your skills across 16 categories. Keep them private for 
                  personal use, or share them publicly as downloadable packages. 
                  Build your reputation in the marketplace.
                </p>
              </div>
              
              <div 
                className="relative rounded-2xl overflow-hidden bg-white"
                style={{
                  boxShadow: `
                    0 20px 25px -5px rgba(0, 0, 0, 0.1),
                    0 50px 100px -20px rgba(0, 0, 0, 0.15),
                    0 30px 60px -30px rgba(229, 127, 63, 0.2)
                  `
                }}
              >
                <img 
                  src="/images/journey/3-organize-share.jpg"
                  alt="Organize & Share - Category selection grid"
                  className="w-full h-auto block"
                  style={{ borderRadius: '1rem' }}
                />
              </div>
            </div>

            {/* Step 4: Deploy - Completion/download screen */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              <div 
                className="relative rounded-2xl overflow-hidden bg-white order-1 md:order-1"
                style={{
                  boxShadow: `
                    0 20px 25px -5px rgba(0, 0, 0, 0.1),
                    0 50px 100px -20px rgba(0, 0, 0, 0.15),
                    0 30px 60px -30px rgba(229, 127, 63, 0.2)
                  `
                }}
              >
                <img 
                  src="/images/journey/4-deploy-dominate.jpg"
                  alt="Deploy & Dominate - Download completion screen"
                  className="w-full h-auto block"
                  style={{ borderRadius: '1rem' }}
                />
              </div>
              
              <div className="order-2 md:order-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-12 w-12 rounded-xl bg-[#e57f3f] text-white flex items-center justify-center text-xl font-bold">
                    4
                  </div>
                </div>
                <h3 className="text-3xl font-bold mb-4">
                  Deploy & Dominate
                </h3>
                <p className="text-lg text-gray-600">
                  Download your skill stack and install directly into Claude. Give 
                  yourself an unfair advantage with your custom skill library. 
                  Explore the marketplace to discover what other creators are building.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Refined */}
      <section id="pricing" className="relative py-32 px-8 overflow-hidden">
        {/* Gradient spotlight on center card - cohesive orange */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-orange-100/20 to-white" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-orange-200/35 via-orange-100/18 to-transparent rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Start free, upgrade when you're ready
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Free Plan */}
            <div className="relative bg-white rounded-3xl p-10 border-2 border-gray-200 hover:border-gray-300 hover:shadow-xl hover:shadow-gray-900/5 transition-all duration-500">
              <h3 className="text-2xl font-bold mb-2 text-gray-900">Free</h3>
              <p className="text-gray-600 mb-8">Perfect to get started</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-600 text-lg">/month</span>
              </div>
              <ul className="space-y-4 mb-10">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Up to 10 skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Claude validation</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Community support</span>
                </li>
              </ul>
              <Button
                onClick={() => navigate('/signup')}
                variant="outline"
                className="w-full py-4 border-2 border-gray-200 rounded-full font-semibold text-base hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                Get Started
              </Button>
            </div>

            {/* Pro Plan - Featured */}
            <div className="relative lg:-mt-4 lg:mb-4">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#e57f3f] to-[#c43408] rounded-3xl blur-xl opacity-25" />
              <div className="relative bg-gradient-to-br from-[#e57f3f] to-[#c43408] rounded-3xl p-10 text-white shadow-2xl shadow-orange-500/25">
                <div className="absolute top-6 right-6">
                  <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold tracking-wide">
                    POPULAR
                  </span>
                </div>
                <h3 className="text-2xl font-bold mb-2">Pro</h3>
                <p className="opacity-90 mb-8">For serious creators</p>
                <div className="mb-8">
                  <span className="text-5xl font-bold">$7</span>
                  <span className="opacity-90 text-lg">/month</span>
                </div>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Unlimited skills</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Monetize your skills</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Priority validation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Advanced analytics</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>Priority support</span>
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/signup')}
                  className="w-full py-4 bg-white text-[#c43408] rounded-full font-semibold text-base hover:bg-gray-50 transition-all shadow-lg"
                >
                  Start Pro Trial
                </Button>
              </div>
            </div>

            {/* Lifetime Plan */}
            <div className="relative bg-white rounded-3xl p-10 border-2 border-gray-200 hover:border-gray-300 hover:shadow-xl hover:shadow-gray-900/5 transition-all duration-500">
              <h3 className="text-2xl font-bold mb-2 text-gray-900">Lifetime</h3>
              <p className="text-gray-600 mb-8">One-time payment, forever access</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-gray-900">$97</span>
                <span className="text-gray-600 text-lg">&nbsp;once</span>
              </div>
              <ul className="space-y-4 mb-10">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Unlimited skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Monetize your skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Priority validation</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Advanced analytics</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Priority support</span>
                </li>
              </ul>
              <Button
                onClick={() => navigate('/signup')}
                variant="outline"
                className="w-full py-4 border-2 border-gray-200 rounded-full font-semibold text-base hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                Get Lifetime Access
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Elegant */}
      <section className="relative py-32 px-8 overflow-hidden">
        {/* Soft gradient overlay - cohesive orange */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 via-orange-100/15 to-white" />
        <div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] bg-gradient-radial from-orange-200/25 via-orange-100/12 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 left-1/4 w-[500px] h-[500px] bg-gradient-radial from-orange-200/20 via-orange-100/10 to-transparent rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Loved by Creators
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Join thousands building the future of Claude workflows
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              { 
                name: 'Sarah Chen', 
                role: 'Head of AI @ TechCo', 
                avatar: 'https://images.unsplash.com/photo-1629425733761-caae3b5f2e50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NjI3NzM3NjR8MA&ixlib=rb-4.1.0&q=85', 
                text: '"Skillerate transformed how our team uses Claude. We\'ve packaged our best workflows into reusable skills that anyone can access."' 
              },
              { 
                name: 'Marcus Rodriguez', 
                role: 'Indie Developer', 
                avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NjI3NzM3NjR8MA&ixlib=rb-4.1.0&q=85', 
                text: '"I\'ve built 12 skills in a month. Each one saves hours every week. It\'s like having an army of Claude assistants working for me."' 
              },
              { 
                name: 'Emma Thompson', 
                role: 'Content Strategist', 
                avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdHxlbnwwfHx8fDE3NjI3NzM3NjR8MA&ixlib=rb-4.1.0&q=85', 
                text: '"The validation system is brilliant. Every skill works exactly as described. No more broken prompts or unpredictable results."' 
              }
            ].map((testimonial, i) => (
              <div key={i} className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-100/50 to-gray-50/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
                <div className="relative bg-white rounded-3xl p-10 border border-gray-100 shadow-lg shadow-gray-900/5 hover:shadow-xl hover:shadow-gray-900/10 transition-all duration-500">
                  <div className="flex items-center gap-1 mb-6">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-5 h-5 fill-[#e57f3f] text-[#e57f3f]" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed text-lg mb-8">
                    {testimonial.text}
                  </p>
                  <div className="flex items-center gap-4">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover shadow-lg"
                    />
                    <div>
                      <p className="font-semibold text-gray-900 text-lg">{testimonial.name}</p>
                      <p className="text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ - Spacious */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Everything you need to know about Skillerate
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-6">
            {[
              {
                q: 'Do I need coding skills to build a Claude Skill?',
                a: 'Not at all! Skillerate\'s visual builder lets you create powerful Claude Skills without writing a single line of code. Just describe what you want your skill to do, and our platform handles the technical implementation.'
              },
              {
                q: 'How does skill validation work?',
                a: 'Every skill you create is automatically tested by Claude itself. We verify that your skill produces the expected outputs, handles edge cases correctly, and follows best practices. Only skills that pass our validation checks can be published.'
              },
              {
                q: 'Can I make my skills private?',
                a: 'Yes! You have full control over your skill\'s visibility. Keep them private for personal use, share them with specific teams, make them unlisted (accessible only via link), or publish them publicly to the marketplace.'
              },
              {
                q: 'What happens if I cancel my subscription?',
                a: 'You\'ll continue to have access to all Pro features until the end of your billing period. After that, you\'ll be automatically moved to the Free plan. Your existing skills will remain published, but you\'ll be limited to 3 skills moving forward.'
              },
              {
                q: 'Can I monetize my skills?',
                a: 'Skill monetization is coming soon! We\'re building a system that will let creators earn from their published skills. Join our waitlist to be notified when this feature launches.'
              }
            ].map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white border-2 border-gray-100 rounded-2xl px-8 hover:border-gray-200 transition-colors">
                <AccordionTrigger className="text-xl font-semibold text-gray-900 hover:text-[#c43408] py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed text-lg pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA - Contained Card Style */}
      <section className="py-32 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#e57f3f] via-[#d05524] to-[#c43408] p-16 lg:p-20">
            {/* Subtle Pattern Overlay */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.3) 1px, transparent 0)',
                backgroundSize: '48px 48px'
              }} />
            </div>
            
            <div className="relative text-center">
              <h2 className="text-5xl lg:text-6xl font-bold mb-8 text-white tracking-tight">
                Ready to Build Your First Skill?
              </h2>
              <p className="text-xl lg:text-2xl mb-12 text-white/90 leading-relaxed max-w-3xl mx-auto">
                Join thousands of creators amplifying Claude's capabilities.
                Start building in minutes—no credit card required.
              </p>

              <Button
                onClick={() => navigate('/signup')}
                className="px-10 py-5 bg-white text-[#c43408] rounded-full font-bold text-lg hover:bg-gray-50 transition-all duration-300 inline-flex items-center gap-3 shadow-2xl shadow-black/20"
              >
                Get Started Free
                <ArrowRight className="w-6 h-6" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Clean & Professional */}
      <footer className="py-20 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 mb-16">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img src="/skillerate-icon.svg" alt="Skillerate" className="w-10 h-10" />
                <span className="text-xl font-bold text-gray-900">Skillerate</span>
              </div>
              <p className="text-gray-600 mb-6 max-w-sm leading-relaxed">
                The curated marketplace for Claude Skills. Built by creators, for creators.
              </p>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="font-bold mb-6 text-gray-900 text-sm uppercase tracking-wider">Product</h3>
              <ul className="space-y-4">
                <li><button onClick={() => scrollToSection('pricing')} className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate('/docs')} className="text-gray-600 hover:text-gray-900 transition-colors">Documentation</button></li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="font-bold mb-6 text-gray-900 text-sm uppercase tracking-wider">Company</h3>
              <ul className="space-y-4">
                <li><a href="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</a></li>
                <li><a href="/blog" className="text-gray-600 hover:text-gray-900 transition-colors">Blog</a></li>
                <li><a href="/careers" className="text-gray-600 hover:text-gray-900 transition-colors">Careers</a></li>
                <li><a href="/contact" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="font-bold mb-6 text-gray-900 text-sm uppercase tracking-wider">Legal</h3>
              <ul className="space-y-4">
                <li><a href="/privacy" className="text-gray-600 hover:text-gray-900 transition-colors">Privacy</a></li>
                <li><a href="/terms" className="text-gray-600 hover:text-gray-900 transition-colors">Terms</a></li>
                <li><a href="/security" className="text-gray-600 hover:text-gray-900 transition-colors">Security</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-10 border-t border-gray-100 text-center">
            <p className="text-gray-600 text-sm">
              © 2025 Skillerate. Built with Claude.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default PremiumLandingPage;